// Castintech dashboard — Sağlık Özeti topbar widget.
// Faz 46.F.3 — Paperclip canlı metrik (issues + agents) topbar pills.
// Backend: GET /castintech-api/health-summary (Faz 46.F.3 endpoint)
//
// View değil — topbar'da sürekli görünür. main.ts startup + 30s timer ile
// refresh. State'e kaydedilmez; module-local cache.

import { apiFetch } from '../api.js';

interface HealthIssues {
  blocked: number;
  open_total: number;
  in_progress: number;
  done: number;
  all_total: number;
}

interface HealthAgents {
  error_count: number;
  paused_count: number;
  busy_count: number;
  idle_count: number;
  total_count: number;
}

interface HealthSummary {
  meta: { generated_at: string };
  issues: HealthIssues;
  agents: HealthAgents;
}

let timer: ReturnType<typeof setInterval> | null = null;
const REFRESH_MS = 30_000; // 30s

// F4-A — Pill state vurgusu (loading|ok|error). Topbar dar olduğu için state-display
// helper'larını DOM'a basmıyoruz; bunun yerine pill'lere data-attribute + tooltip
// veriyoruz (renderError artık sebep + retry hint açıklar).

export async function loadHealthSummary(): Promise<void> {
  setLoadingState();
  try {
    const r = await apiFetch('/castintech-api/health-summary');
    if (!r.ok) {
      renderError(`HTTP ${r.status} — endpoint yanıt vermiyor`);
      return;
    }
    const data = (await r.json()) as HealthSummary;
    renderHealth(data);
  } catch (e) {
    console.error('[health-summary] fetch error', e);
    renderError((e as Error).message || 'Bağlantı kurulamadı');
  }
}

function setText(id: string, value: string): void {
  const el = document.getElementById(id);
  if (el) el.textContent = value;
}

function setTooltip(id: string, text: string): void {
  const el = document.getElementById(id);
  if (el) el.setAttribute('title', text);
}

// Topbar pill'lerini loading state'ine al — kullanıcı ilk açılışta veya 30s refresh
// sırasında "?" görmesin, "..." görsün; tooltip "Yükleniyor".
function setLoadingState(): void {
  for (const id of ['hp-issues-val', 'hp-blocked-val', 'hp-errors-val', 'hp-busy-val']) {
    const el = document.getElementById(id);
    if (el && el.textContent === '?') el.textContent = '…';
  }
  for (const pillId of ['hp-issues', 'hp-blocked', 'hp-errors', 'hp-busy']) {
    setTooltip(pillId, 'Yükleniyor…');
  }
}

function setPillClass(pillId: string, severity: 'ok' | 'warn' | 'crit'): void {
  const el = document.getElementById(pillId);
  if (!el) return;
  el.classList.remove('hp-ok', 'hp-warn', 'hp-crit');
  el.classList.add(`hp-${severity}`);
}

function renderHealth(data: HealthSummary): void {
  const i = data.issues;
  const a = data.agents;

  // F4-A — Başarılı render: önceki error/loading tooltip'lerini temizle.
  const tsLabel = data.meta?.generated_at ? `Son güncelleme: ${data.meta.generated_at}` : 'Canlı';
  for (const pillId of ['hp-issues', 'hp-blocked', 'hp-errors', 'hp-busy']) {
    setTooltip(pillId, tsLabel);
  }

  // Open issues pill: "blocked / open"
  setText('hp-issues-val', `${i.in_progress}/${i.open_total}`);
  // Blocked pill — kritiklik: %50+ blocked = crit, %30+ = warn
  const blockedRatio = i.open_total > 0 ? i.blocked / i.open_total : 0;
  setText('hp-blocked-val', String(i.blocked));
  setPillClass('hp-blocked', blockedRatio >= 0.5 ? 'crit' : blockedRatio >= 0.3 ? 'warn' : 'ok');

  // Errors pill — herhangi error = crit
  setText('hp-errors-val', String(a.error_count));
  setPillClass('hp-errors', a.error_count > 0 ? 'crit' : 'ok');

  // Busy/running — 0 ise warn (ajanlar çalışmıyor)
  setText('hp-busy-val', `${a.busy_count}/${a.total_count}`);
  setPillClass('hp-busy', a.busy_count === 0 ? 'warn' : 'ok');

  // Issues default
  setPillClass('hp-issues', 'ok');
}

function renderError(reason?: string): void {
  setText('hp-issues-val', '?');
  setText('hp-blocked-val', '?');
  setText('hp-errors-val', '?');
  setText('hp-busy-val', '?');
  // F4-A — Tooltip + retry hint. Topbar widget'ı yer dar olduğu için pill'lere
  // hover tooltip ekliyoruz; 30s sonra otomatik retry edileceğini söylüyoruz.
  const msg = reason
    ? `Sağlık özeti alınamadı: ${reason}. 30 sn içinde tekrar denenecek.`
    : 'Sağlık özeti endpoint\'i yanıt vermiyor. 30 sn içinde tekrar denenecek.';
  for (const pillId of ['hp-issues', 'hp-blocked', 'hp-errors', 'hp-busy']) {
    setTooltip(pillId, msg);
    // Görsel uyarı: tüm pill'leri crit class'ına al
    const el = document.getElementById(pillId);
    if (el) {
      el.classList.remove('hp-ok', 'hp-warn');
      el.classList.add('hp-crit');
    }
  }
}

export function startHealthSummaryTimer(): void {
  if (timer) return;
  loadHealthSummary().catch(() => {});
  timer = setInterval(() => loadHealthSummary().catch(() => {}), REFRESH_MS);
}

export function stopHealthSummaryTimer(): void {
  if (timer) {
    clearInterval(timer);
    timer = null;
  }
}
